/* Joggy purchase selector. Prices and plan identifiers come only from Shopify Liquid. */
(function (root) {
  'use strict';

  const cadence = (value) => String(value || '').trim().toLowerCase().replace(/^days?:?(\d+)$/, 'day:$1');
  const sameId = (left, right) => String(left || '') === String(right || '');
  const moneyValue = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  const validId = (value) => /^[1-9]\d*$/.test(String(value || ''));
  const validPlans = (variant) => (variant?.plans || []).filter((plan) =>
    validId(plan.id) && moneyValue(plan.price) && moneyValue(plan.renewalPrice) && plan.label && plan.cadenceKey
  );

  function defaultPlan(variant) {
    const plans = validPlans(variant);
    return plans.find((plan) => /^(day|days):28$/.test(cadence(plan.cadenceKey)))
      || plans.find((plan) => /^(day|days):30$/.test(cadence(plan.cadenceKey)))
      || plans[0] || null;
  }

  function choosePlan(variant, request = {}, allowFallback = false) {
    const plans = validPlans(variant);
    if (request.planId) {
      const exact = plans.find((plan) => sameId(plan.id, request.planId));
      if (exact) return exact;
      if (!allowFallback) return null;
    }
    if (request.cadenceKey) {
      const matching = plans.find((plan) => cadence(plan.cadenceKey) === cadence(request.cadenceKey));
      if (matching) return matching;
      if (!allowFallback) return null;
    }
    return defaultPlan(variant);
  }

  function getFlavor(data, key) {
    return data.flavors.find((flavor) => flavor.key === key) || data.flavors[0];
  }

  function packForFlavor(flavor, pack) {
    return flavor.key === 'variety-pack' && Number(pack) === 24 ? 12 : Number(pack);
  }

  function deriveState(data, requested) {
    const flavor = getFlavor(data, requested.flavorKey);
    const pack = Number(requested.pack);
    const mode = requested.mode === 'onetime' ? 'onetime' : 'subscription';
    const variant = flavor?.variants.find((item) => Number(item.pack) === pack) || null;
    const plan = choosePlan(variant, requested, false);
    const price = mode === 'subscription' ? plan?.price ?? null : variant?.price ?? null;
    let issue = '';
    if (!variant || !validId(variant.id) || !moneyValue(variant.price)) {
      issue = `${pack}-Pack is not available in ${flavor?.label || 'this flavor'}. Choose an available pack size.`;
    } else if (!variant.available) {
      issue = `${flavor.label} ${pack}-Pack is sold out. Choose an available pack size.`;
    } else if (mode === 'onetime' && variant.requiresSellingPlan) {
      issue = 'This pack requires a subscription. Select Subscribe & Save.';
    } else if (mode === 'subscription' && !validPlans(variant).length) {
      issue = 'A subscription is not available for this pack. Select One-Time Purchase or another pack size.';
    } else if (mode === 'subscription' && !plan) {
      issue = 'Choose an available delivery schedule before adding to your cart.';
    }

    const cards = [3, 12, 24].map((size) => {
      const item = flavor?.variants.find((entry) => Number(entry.pack) === size);
      const cardPlan = size === pack && mode === 'subscription'
        ? plan
        : choosePlan(item, { cadenceKey: plan?.cadenceKey || requested.cadenceKey }, true);
      const cardPrice = mode === 'subscription' ? cardPlan?.price : item?.price;
      const available = Boolean(item?.available && validId(item.id) && moneyValue(item.price)
        && (mode === 'subscription' ? cardPlan : !item.requiresSellingPlan));
      return {
        pack: size, variant: item || null, plan: cardPlan, available,
        price: moneyValue(cardPrice) ? cardPrice : null,
        regularPrice: item?.price ?? null,
        perCan: moneyValue(cardPrice) ? cardPrice / size : null,
      };
    });
    const twelve = cards.find((card) => card.pack === 12);
    const twentyFour = cards.find((card) => card.pack === 24);
    return {
      flavor, pack, mode, variant, plan, price, cards, issue,
      canAdd: !issue && moneyValue(price),
      sellingPlanId: mode === 'subscription' && plan && !issue ? String(plan.id) : '',
      bestValue: Boolean(twelve.available && twentyFour.available && twentyFour.perCan < twelve.perCan),
    };
  }

  function initialState(data, href) {
    const params = new URL(href).searchParams;
    const initialVariant = data.flavors.flatMap((flavor) => flavor.variants)
      .find((variant) => sameId(variant.id, data.currentVariantId));
    const flavor = getFlavor(data, data.currentFlavor);
    const requestedPack = Number(params.get('pack'));
    const originalPack = Number.isInteger(requestedPack) && requestedPack > 0
      ? requestedPack : Number(initialVariant?.pack || 12);
    const pack = packForFlavor(flavor, originalPack);
    const packChanged = pack !== originalPack;
    const variant = flavor.variants.find((item) => Number(item.pack) === pack);
    const mode = params.get('purchase_mode') === 'onetime' ? 'onetime' : 'subscription';
    const suppliedPlan = params.get('selling_plan');
    const suppliedCadence = params.get('cadence');
    const previousPackPlan = packChanged && suppliedPlan && data.flavors.some((item) => item.variants.some((entry) =>
      Number(entry.pack) === originalPack && validPlans(entry).some((plan) => sameId(plan.id, suppliedPlan))
    ));
    const plan = choosePlan(variant, { planId: previousPackPlan ? '' : suppliedPlan, cadenceKey: suppliedCadence }, !suppliedPlan || previousPackPlan);
    const notices = [];
    if (packChanged) notices.push('Variety Pack is available as a 12-Pack. The 12-Pack is selected.');
    if (mode === 'subscription' && suppliedCadence && plan && cadence(suppliedCadence) !== cadence(plan.cadenceKey)) {
      notices.push(`The previous delivery schedule is unavailable for this pack. ${plan.label} is selected.`);
    }
    return {
      flavorKey: flavor.key, pack,
      mode,
      planId: suppliedPlan && !plan ? suppliedPlan : plan?.id || '',
      cadenceKey: plan?.cadenceKey || suppliedCadence || '',
      notice: notices.join(' '),
      packAdjusted: packChanged,
    };
  }

  function navigationUrl(href, flavor, request) {
    const current = new URL(href);
    const destination = new URL(flavor.url, current);
    current.searchParams.forEach((value, key) => destination.searchParams.set(key, value));
    const pack = packForFlavor(flavor, request.pack);
    const variant = flavor.variants.find((item) => Number(item.pack) === pack);
    const fallbackVariant = flavor.key === 'variety-pack'
      ? flavor.variants.find((item) => Number(item.pack) === 12)
      : flavor.variants.find((item) => item.available) || flavor.variants[0];
    const plan = choosePlan(variant, { cadenceKey: request.cadenceKey }, true);
    const id = variant?.id || fallbackVariant?.id;
    if (validId(id)) destination.searchParams.set('variant', String(id));
    else destination.searchParams.delete('variant');
    destination.searchParams.set('pack', String(pack));
    destination.searchParams.set('purchase_mode', request.mode === 'onetime' ? 'onetime' : 'subscription');
    if (request.cadenceKey) destination.searchParams.set('cadence', request.cadenceKey);
    else destination.searchParams.delete('cadence');
    if (request.mode !== 'onetime' && plan && variant) destination.searchParams.set('selling_plan', String(plan.id));
    else destination.searchParams.delete('selling_plan');
    return destination;
  }

  function offerText(state) {
    if (!state.variant || !state.plan || state.plan.price >= state.variant.price) return 'Automatic delivery';
    const discount = Math.round((1 - state.plan.price / state.variant.price) * 1000) / 10;
    if (state.plan.price === state.plan.renewalPrice) return `Save ${discount}% on every subscription order`;
    const count = Number(state.plan.firstOrderCount);
    if (count === 1) return `${discount}% off + free shipping`;
    if (Number.isInteger(count) && count > 1) return `${discount}% off your first ${count} subscription orders`;
    return 'Subscription savings';
  }

  const helpers = { validPlans, defaultPlan, choosePlan, deriveState, initialState, navigationUrl, offerText };
  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (!root.customElements || !root.HTMLElement || root.customElements.get('joggy-purchase-selector')) return;

  class JoggyPurchaseSelector extends root.HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.data = JSON.parse(this.querySelector('[data-selector-data]').textContent);
        this.request = initialState(this.data, root.location.href);
        this.form = this.querySelector('product-form form');
        this.submit = this.form?.querySelector('[type="submit"]');
        if (!this.form || !this.submit) throw new Error('Purchase form is unavailable.');
        this.formatter = new Intl.NumberFormat(this.data.locale || 'en-US', {
          style: 'currency', currency: this.data.currency || 'USD',
        });
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('click', this.onClick.bind(this));
        this.form.addEventListener('submit', this.validateSubmit.bind(this), true);
        this.render();
        if (this.request.packAdjusted && deriveState(this.data, this.request).canAdd) this.updateUrl();
      } catch (error) {
        this.querySelector('[data-status]')?.replaceChildren('The purchase options could not load. Please refresh the page.');
        this.querySelector('[type="submit"]')?.setAttribute('disabled', 'disabled');
        console.error('Joggy purchase selector:', error);
      }
    }

    money(cents) { return this.formatter.format(cents / 100); }
    text(selector, value) {
      const node = this.querySelector(selector);
      if (node) node.textContent = value;
    }
    hide(selector, hidden) {
      const node = this.querySelector(selector);
      if (node) node.hidden = hidden;
    }
    get busy() {
      return this.submit?.classList.contains('loading') || this.submit?.getAttribute('aria-disabled') === 'true';
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches('[data-pack], [data-mode], [data-plan], [data-plan-choice]')) return;
      if (this.busy) {
        this.render();
        this.text('[data-status]', 'Adding to your cart. Please wait before changing your selection.');
        return;
      }
      const before = deriveState(this.data, this.request);
      this.request.notice = '';
      if (input.matches('[data-pack]')) {
        this.request.pack = Number(input.value);
        const variant = before.flavor.variants.find((item) => Number(item.pack) === this.request.pack);
        const nextPlan = choosePlan(variant, { cadenceKey: before.plan?.cadenceKey || this.request.cadenceKey }, true);
        if (this.request.mode === 'subscription' && before.plan && nextPlan
            && cadence(before.plan.cadenceKey) !== cadence(nextPlan.cadenceKey)) {
          this.request.notice = `The previous delivery schedule is unavailable for this pack. ${nextPlan.label} is selected.`;
        }
        this.request.planId = nextPlan?.id || '';
        this.request.cadenceKey = nextPlan?.cadenceKey || this.request.cadenceKey;
      } else if (input.matches('[data-mode]')) {
        this.request.mode = input.value === 'onetime' ? 'onetime' : 'subscription';
      } else {
        const plan = validPlans(before.variant).find((item) => sameId(item.id, input.value));
        this.request.planId = plan?.id || input.value;
        this.request.cadenceKey = plan?.cadenceKey || '';
      }
      this.querySelector('product-form')?.handleErrorMessage?.();
      this.render();
      this.updateUrl();
    }

    onClick(event) {
      const anchor = event.target.closest('a[data-flavor]');
      if (!anchor || !this.contains(anchor) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const flavor = this.data.flavors.find((item) => item.key === anchor.dataset.flavor);
      if (!flavor) return;
      event.preventDefault();
      if (this.busy) {
        this.text('[data-status]', 'Adding to your cart. Please wait before changing your selection.');
        return;
      }
      if (flavor.key === this.request.flavorKey) return;
      root.location.assign(navigationUrl(root.location.href, flavor, this.request).href);
    }

    updateUrl() {
      const state = deriveState(this.data, this.request);
      const url = navigationUrl(root.location.href, state.flavor, this.request);
      // An invalid delivery choice must remain invalid until the customer chooses a real plan.
      if (state.mode === 'subscription' && !state.plan) url.searchParams.delete('selling_plan');
      root.history.replaceState(root.history.state, '', url.href);
    }

    validateSubmit(event) {
      const state = deriveState(this.data, this.request);
      if (!state.canAdd || this.busy) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.text('[data-status]', state.issue || 'Adding to your cart. Please wait.');
        return;
      }
      this.syncForm(state);
    }

    syncForm(state) {
      const id = this.form.querySelector('[name="id"]');
      const sellingPlan = this.form.querySelector('[name="selling_plan"]');
      const quantity = this.form.querySelector('[name="quantity"]');
      if (!id || !sellingPlan || !quantity) throw new Error('Purchase form fields are unavailable.');
      const nextId = state.variant && validId(state.variant.id) ? String(state.variant.id) : '';
      const variantChanged = id.value !== nextId;
      id.value = nextId;
      id.disabled = !state.canAdd;
      if (variantChanged) id.dispatchEvent(new Event('change', { bubbles: true }));
      const planChanged = sellingPlan.value !== state.sellingPlanId
        || sellingPlan.disabled !== (state.mode !== 'subscription' || !state.sellingPlanId);
      sellingPlan.value = state.sellingPlanId;
      sellingPlan.disabled = state.mode !== 'subscription' || !state.sellingPlanId;
      if (planChanged) sellingPlan.dispatchEvent(new Event('change', { bubbles: true }));
      quantity.value = String(Math.max(1, Number(state.variant?.quantityMin) || 1));
    }

    render() {
      const state = deriveState(this.data, this.request);
      this.dataset.purchaseMode = state.mode;
      this.querySelectorAll('[data-pack]').forEach((input) => {
        input.checked = Number(input.value) === state.pack;
        const variant = state.flavor.variants.find((item) => Number(item.pack) === Number(input.value));
        input.disabled = !variant?.available;
      });
      this.querySelectorAll('[data-mode]').forEach((input) => {
        input.checked = input.value === state.mode;
        input.disabled = input.value === 'subscription'
          ? !validPlans(state.variant).length : Boolean(state.variant?.requiresSellingPlan);
      });
      this.querySelectorAll('a[data-flavor]').forEach((anchor) => {
        const flavor = this.data.flavors.find((item) => item.key === anchor.dataset.flavor);
        if (flavor) anchor.href = navigationUrl(root.location.href, flavor, this.request).href;
        if (flavor?.key === state.flavor.key) anchor.setAttribute('aria-current', 'true');
        else anchor.removeAttribute('aria-current');
      });
      const sampleLink = this.querySelector('[data-sample-link]');
      if (sampleLink) {
        const sampleUrl = new URL(sampleLink.getAttribute('href'), root.location.href);
        const currentUrl = new URL(root.location.href);
        currentUrl.searchParams.forEach((value, key) => {
          if (!['variant', 'selling_plan', 'cadence', 'pack', 'purchase_mode'].includes(key)) sampleUrl.searchParams.set(key, value);
        });
        sampleLink.href = sampleUrl.href;
      }

      state.cards.forEach((card) => {
        const element = this.querySelector(`[data-pack-card="${card.pack}"]`);
        if (!element) return;
        element.dataset.selected = String(card.pack === state.pack);
        element.dataset.available = String(card.available);
        const set = (selector, text) => { const node = element.querySelector(selector); if (node) node.textContent = text; };
        set('[data-pack-price]', card.price === null ? 'Unavailable' : this.money(card.price));
        set('[data-pack-unit]', card.perCan === null ? '' : `${this.money(card.perCan)} / can`);
        const discounted = state.mode === 'subscription' && card.price !== null && card.price < card.regularPrice;
        set('[data-pack-regular]', discounted ? this.money(card.regularPrice) : '');
        const regular = element.querySelector('[data-pack-regular]');
        if (regular) regular.hidden = !discounted;
        set('[data-pack-availability]', !card.variant ? 'Not available in this flavor' : !card.variant.available
          ? 'Sold out' : state.mode === 'subscription' && !card.plan ? 'One-time purchase only' : '');
      });
      this.hide('[data-best-value]', !state.bestValue);
      this.text('[data-sub-price]', state.plan ? this.money(state.plan.price) : 'Unavailable');
      this.hide('[data-sub-price]', state.mode === 'onetime');
      this.text('[data-sub-regular]', state.variant && state.plan && state.plan.price < state.variant.price ? this.money(state.variant.price) : '');
      this.hide('[data-sub-regular]', !state.variant || !state.plan || state.plan.price >= state.variant.price || state.mode === 'onetime');
      this.text('[data-one-price]', state.variant ? this.money(state.variant.price) : 'Unavailable');
      this.text('[data-offer]', state.mode === 'subscription' && state.plan ? offerText(state) : '');
      this.hide('[data-offer]', state.mode !== 'subscription' || !state.plan);
      this.hide('[data-offer-strip]', state.mode !== 'subscription' || !state.plan);
      this.text('[data-can-description]', state.mode === 'subscription' && state.variant && state.plan
        ? `${state.pack} cans, delivered on your schedule` : '');
      this.hide('[data-can-description]', state.mode !== 'subscription' || !state.variant || !state.plan);
      this.hide('[data-delivery-controls]', state.mode !== 'subscription' || !validPlans(state.variant).length);
      this.querySelector('[data-subscription-panel]')?.setAttribute('data-selected', String(state.mode === 'subscription'));
      this.querySelector('[data-onetime-panel]')?.setAttribute('data-selected', String(state.mode === 'onetime'));

      const select = this.querySelector('[data-plan]');
      if (select) {
        const signature = JSON.stringify(validPlans(state.variant).map((plan) => [String(plan.id), plan.label]));
        if (select.dataset.planSignature !== signature || (!state.plan && !select.querySelector('option[value=""]'))) {
          select.replaceChildren();
          if (!state.plan) select.add(new Option('Choose a delivery schedule', ''));
          validPlans(state.variant).forEach((plan) => select.add(new Option(plan.label, String(plan.id))));
          select.dataset.planSignature = signature;
        }
        select.value = state.plan ? String(state.plan.id) : '';
        select.disabled = state.mode !== 'subscription' || !validPlans(state.variant).length;
      }
      const scheduleOptions = this.querySelector('[data-schedule-options]');
      if (scheduleOptions) {
        const plans = validPlans(state.variant);
        const signature = JSON.stringify(plans.map((plan) => [String(plan.id), plan.label]));
        scheduleOptions.setAttribute('role', 'radiogroup');
        if (!scheduleOptions.hasAttribute('aria-labelledby') && !scheduleOptions.hasAttribute('aria-label')) {
          scheduleOptions.setAttribute('aria-label', 'Delivery schedule');
        }
        if (scheduleOptions.dataset.planSignature !== signature) {
          scheduleOptions.replaceChildren();
          plans.forEach((plan) => {
            const label = this.ownerDocument.createElement('label');
            label.className = 'jps__schedule';
            const radio = this.ownerDocument.createElement('input');
            radio.type = 'radio';
            radio.name = `jps-cadence-${this.form.id || this.data.productId}`;
            radio.value = String(plan.id);
            radio.setAttribute('data-plan-choice', '');
            const text = this.ownerDocument.createElement('span');
            text.className = 'jps__schedule-label';
            text.textContent = plan.label;
            label.append(radio, text);
            scheduleOptions.append(label);
          });
          scheduleOptions.dataset.planSignature = signature;
        }
        scheduleOptions.querySelectorAll('[data-plan-choice]').forEach((radio) => {
          radio.checked = Boolean(state.plan && sameId(radio.value, state.plan.id));
          radio.disabled = state.mode !== 'subscription';
        });
      }
      let renewal = '';
      if (state.mode === 'subscription' && state.plan && state.variant) {
        const count = Number(state.plan.firstOrderCount);
        const schedule = state.plan.label.charAt(0).toLowerCase() + state.plan.label.slice(1);
        renewal = state.plan.price === state.plan.renewalPrice
          ? `Renews at ${this.money(state.plan.renewalPrice)} ${schedule}. Cancel anytime.`
          : `${this.money(state.plan.price)} ${count > 1 ? `for each of your first ${count} shipments` : count === 1 ? 'for your first shipment' : 'today'}; renews at ${this.money(state.plan.renewalPrice)} ${schedule}. Cancel anytime.`;
      }
      this.text('[data-renewal]', renewal);
      this.hide('[data-renewal]', !renewal);
      this.text('[data-status]', state.issue || this.request.notice || '');
      this.text('.buy-button__label', state.canAdd ? 'Add to Cart' : state.variant && !state.variant.available ? 'Sold Out' : 'Unavailable');
      this.text('.buy-button__price', state.canAdd ? this.money(state.price) : '');
      if (!this.busy) this.submit.disabled = !state.canAdd;
      this.syncForm(state);
    }
  }
  root.customElements.define('joggy-purchase-selector', JoggyPurchaseSelector);
})(typeof window !== 'undefined' ? window : globalThis);
